import { Icon } from "components/Icon/Icon";
import { IconCollection } from "components/Icon/IconCollection";
import StatusBadge from "components/StatusBadge";
import OverflowTooltip from "components/Tooltip/OverflowTooltip";
import Tooltip from "components/Tooltip/Tooltip";
import { getIsReadonly } from "config/resourceUtils";
import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { getRelativeUrl } from "utils/service";
import styles from "./Components.module.scss";

export const ControlIcon = ({
    hasValue,
    icon,
    iconColor,
}: {
    hasValue: boolean;
    icon: any;
    iconColor?: string;
}) => {
    return (
        <div className={`${styles.iconControl} ${hasValue && iconColor ? styles[iconColor] : ""} `}>
            <Icon icon={icon} />
        </div>
    );
};

export const iconWithTooltipClassName = "iconWithTooltip";

export const IconWithTooltip = ({
    inner,
    isDisabled,
    overlay,
    align,
}: {
    overlay: string;
    inner: ReactNode;
    isDisabled?: boolean;
    align?: { offset: number[] };
}) => (
    <div style={{ position: "relative" }}>
        {inner}
        <Tooltip overlay={overlay} align={align}>
            <div
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    display: "block",
                    width: "24px",
                    height: "24px",
                    opacity: 0,
                    pointerEvents: "auto",
                    cursor: "pointer",
                }}
                className={iconWithTooltipClassName}
                onClick={(e) => isDisabled && e.stopPropagation()}
            />
        </Tooltip>
    </div>
);

/**
 * Canvas read-only affordance. On the canvas the read-only state is shown as a
 * single small, neutral (non-yellow) lock icon with a "Read-only" tooltip that
 * fires for ANY read-only reason (record / project / bundle) without exposing the
 * source — the source detail is unnecessary noise on the canvas node (Margot's
 * canvas-review decision). Replaces the yellow READ-ONLY* StatusBadge here only;
 * the shared getReadonlyBadges stays as-is for the full resource pages.
 */
export const getCanvasReadonlyAffordance = ({ resourceData, resourceConfig }) => {
    if (!getIsReadonly(resourceConfig, resourceData)) {
        return null;
    }
    return (
        <Tooltip overlay="Read-only">
            <span className={styles.readonlyAffordance} aria-label="Read-only">
                <Icon icon={IconCollection.lockOutline} />
            </span>
        </Tooltip>
    );
};

export const getAnotherLocationBadge = ({ currentLocationId, resourceData, size = "" }) => {
    if (
        currentLocationId === resourceData?.project_id ||
        currentLocationId === resourceData?.bundle_id
    ) {
        return null;
    }
    const location = resourceData?.project_id_project
        ? "project"
        : resourceData?.bundle_id_bundle
        ? "bundle"
        : undefined;
    const locationName =
        resourceData?.project_id_project?.name ?? resourceData?.bundle_id_bundle?.name;

    if (location) {
        const text = `in ${location} "${locationName}"`;
        const textContent = (
            <span className={styles.locationBadge}>
                <OverflowTooltip overlay={text} isOverlapping={false}>
                    <span>{text}</span>
                </OverflowTooltip>{" "}
                <Link
                    to={getRelativeUrl(
                        location,
                        resourceData?.project_id ?? resourceData?.bundle_id
                    )}
                >
                    <Icon icon={IconCollection.openInNew} />
                </Link>
            </span>
        );
        return <StatusBadge type="primary-yellow" text={textContent} size={size} />;
    }
    return null;
};
